import * as Phaser from 'phaser';
import { createLogger } from '../utils/Logger';
import { RuntimeTestingModeState } from '../config/RuntimeTestingMode';
import { DebugCommandResult, DebugInventorySnapshot, DebugWaveActionSnapshot } from '../systems/DebugCommandTypes';
import type { PlayerStatsConfig } from '../components/PlayerStats';
import { AppVersionSnapshot } from '../config/AppVersion';

const PANEL_WIDTH = 384;
const PANEL_HEIGHT = 466;
const PANEL_PADDING = 10;
const TEXT_WIDTH = PANEL_WIDTH - PANEL_PADDING * 2;
const TEXT_HEIGHT = PANEL_HEIGHT - PANEL_PADDING * 2;
const MAX_SCALING_ENEMY_ID_LENGTH = 18;
const MAX_SCALING_MODIFIER_ID_LENGTH = 6;
const MAX_SCALING_MODIFIERS = 3;
const MAX_SUMMARY_LINE_LENGTH = 56;
const MAX_BOSS_LABEL_LENGTH = 22;
const MAX_DEBUG_COMMAND_TEXT_LENGTH = 24;
const MAX_DEBUG_REASON_TEXT_LENGTH = 24;
const MAX_DEBUG_WEAPON_ID_LENGTH = 12;
const MAX_DEBUG_PASSIVE_ID_LENGTH = 12;
const MAX_VISUAL_KEYS = 4;
const MAX_VISUAL_KEY_LENGTH = 12;

export interface DiagnosticsOverlaySnapshot {
  readonly fps: number;
  readonly playerHp: number;
  readonly enemyCount: number;
  readonly waveIndex: number;
  readonly waveTime: number;
  readonly bossActive: boolean;
  readonly bossesSpawned: number;
  readonly bossSpawnTime: number;
  readonly bossesToWin: number;
  readonly bossesKilled: number;
  readonly pauseWaves: boolean;
  readonly activeBossEncounter?: {
    id: string;
    name: string;
  };
  readonly nextBossEncounter?: {
    id: string;
    spawnTime: number;
  };
  readonly scalingSummary?: {
    waveIndex: number;
    enemyId: string;
    modifierIds: readonly string[];
    resolvedStats: {
      health: number;
      maxHealth: number;
      speed: number;
      damage: number;
      xpReward: number;
    };
  };
  readonly poolStats: Record<string, { active: number; total: number; free: number }>;
  readonly testingMode: RuntimeTestingModeState;
  readonly profile?: {
    readonly ok: boolean;
    readonly schemaVersion?: number;
    readonly totalRuns?: number;
    readonly bestSurvivedSeconds?: number;
    readonly lastRunResult?: string;
    readonly unlockCount?: number;
    readonly error?: string;
  };
  readonly inventory?: DebugInventorySnapshot;
  readonly playerStatsSnapshot?: PlayerStatsConfig;
  readonly appVersion?: AppVersionSnapshot;
  readonly visual?: {
    readonly manifestKeys: readonly string[];
    readonly loadedKeys: readonly string[];
    readonly fallbackKeys: readonly string[];
    readonly skippedOptionalKeys: readonly string[];
    readonly failedKeys: readonly string[];
    readonly requiredFailures: readonly string[];
  };
  readonly lastDebugCommandResult?: DebugCommandResult<unknown>;
  readonly lastDebugWaveAction?: DebugWaveActionSnapshot | null;
}

export function shouldEmitProfileDiagnosticsWarning(
  previousSignature: string | null,
  nextSignature: string,
  rawLogLevel: unknown = (globalThis as any).process?.env?.LOG_LEVEL
): boolean {
  return String(rawLogLevel ?? '').toLowerCase() === 'debug' || previousSignature !== nextSignature;
}

export function formatScalingSummaryLines(summary: DiagnosticsOverlaySnapshot['scalingSummary']): string[] {
  if (!summary) {
    return ['scale: n/a'];
  }

  const modifierIds = summary.modifierIds.length > 0
    ? summary.modifierIds
    : ['none'];
  const visibleModifierIds = modifierIds
    .slice(0, MAX_SCALING_MODIFIERS)
    .map(id => truncateLabel(id, MAX_SCALING_MODIFIER_ID_LENGTH));
  const remainingModifierCount = Math.max(0, modifierIds.length - visibleModifierIds.length);
  const modifierText = remainingModifierCount > 0
    ? `${visibleModifierIds.join(',')},+${remainingModifierCount}`
    : visibleModifierIds.join(',');

  const header = `scale: w${summary.waveIndex} ${truncateLabel(summary.enemyId, MAX_SCALING_ENEMY_ID_LENGTH)} [${modifierText}]`;
  const stats = summary.resolvedStats;
  const values = `  hp=${stats.health}/${stats.maxHealth} spd=${stats.speed.toFixed(1)} dmg=${stats.damage} xp=${stats.xpReward}`;

  return [
    truncateLabel(header, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(values, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export function formatBossEncounterSummaryLines(snapshot: DiagnosticsOverlaySnapshot): string[] {
  const activeEncounter = snapshot.activeBossEncounter;
  const nextEncounter = snapshot.nextBossEncounter;

  const activeLine = activeEncounter
    ? `bossNow: ${truncateLabel(activeEncounter.id, MAX_BOSS_LABEL_LENGTH)} ${truncateLabel(activeEncounter.name, MAX_BOSS_LABEL_LENGTH)}`
    : 'bossNow: none';

  const nextLine = nextEncounter
    ? `bossNext: t=${nextEncounter.spawnTime.toFixed(1)} ${truncateLabel(nextEncounter.id, MAX_BOSS_LABEL_LENGTH)}`
    : 'bossNext: none';

  const progressLine = `bossProg: ${snapshot.bossesKilled}/${snapshot.bossesToWin} (spawned=${snapshot.bossesSpawned})`;

  return [
    truncateLabel(activeLine, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(nextLine, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(progressLine, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export function formatDebugCommandSummaryLines(
  result: DiagnosticsOverlaySnapshot['lastDebugCommandResult'],
  waveAction: DiagnosticsOverlaySnapshot['lastDebugWaveAction']
): string[] {
  if (!result) {
    const waveActionText = waveAction
      ? `${waveAction.action}:${waveAction.result}@${waveAction.nextWaveIndex}`
      : 'none';
    return [truncateLabel(`cmd: none waveDbg=${waveActionText}`, MAX_SUMMARY_LINE_LENGTH)];
  }

  if (result.ok) {
    const summary = `cmd: ok ${truncateLabel(result.command, MAX_DEBUG_COMMAND_TEXT_LENGTH)} src=${result.source}`;
    const detail = result.command === 'spawnEnemy'
      ? `  spawn->active=${String((result.data as { activeEnemyCount?: number } | undefined)?.activeEnemyCount ?? 'n/a')}`
      : result.command === 'giveWeapon'
        ? `  give->lvl=${String((result.data as { currentLevel?: number } | undefined)?.currentLevel ?? 'n/a')}`
        : result.command === 'forceWave' || result.command === 'skipWave'
          ? `  wave->${String((result.data as { nextWaveIndex?: number } | undefined)?.nextWaveIndex ?? 'n/a')}`
          : '  stats snapshot ok';
    return [
      truncateLabel(summary, MAX_SUMMARY_LINE_LENGTH),
      truncateLabel(detail, MAX_SUMMARY_LINE_LENGTH)
    ];
  }

  const summary = `cmd: fail ${truncateLabel(result.command, MAX_DEBUG_COMMAND_TEXT_LENGTH)} src=${result.source}`;
  const reason = `  reason=${truncateLabel(result.reason, MAX_DEBUG_REASON_TEXT_LENGTH)}`;
  const waveResult = waveAction ? ` waveDbg=${waveAction.action}:${waveAction.result}` : '';
  return [
    truncateLabel(summary, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(`${reason}${waveResult}`, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export function formatInventorySummaryLines(inventory: DiagnosticsOverlaySnapshot['inventory']): string[] {
  if (!inventory) {
    return ['inv: n/a'];
  }

  const weaponText = inventory.weapons.length > 0
    ? inventory.weapons
      .slice(0, 2)
      .map(weapon => `${truncateLabel(weapon.id, MAX_DEBUG_WEAPON_ID_LENGTH)}:${weapon.level}/${weapon.maxLevel}`)
      .join(',')
    : 'none';
  const passiveText = inventory.passives.length > 0
    ? inventory.passives
      .slice(0, 2)
      .map(passive => `${truncateLabel(passive.id, MAX_DEBUG_PASSIVE_ID_LENGTH)}:${passive.level}/${passive.maxLevel}`)
      .join(',')
    : 'none';

  return [
    truncateLabel(`inv: w=${inventory.activeWeaponCount}/${inventory.maxWeapons} p=${inventory.activePassiveCount}/${inventory.maxPassives}`, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(`  w[${weaponText}] p[${passiveText}]`, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export function formatPlayerStatsSummaryLines(playerStats: DiagnosticsOverlaySnapshot['playerStatsSnapshot']): string[] {
  if (!playerStats) {
    return ['stats: n/a'];
  }

  const health = playerStats.maxHealth ?? 0;
  const damage = playerStats.damageMultiplier ?? 1;
  const speed = playerStats.moveSpeed ?? 0;
  const cooldown = playerStats.cooldownMultiplier ?? 1;
  const pickup = playerStats.pickupRange ?? 0;
  return [
    truncateLabel(`stats: hp=${health} dmg=${damage.toFixed(2)} cd=${cooldown.toFixed(2)}`, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(`  move=${speed.toFixed(1)} pickup=${pickup.toFixed(1)}`, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export function formatAppVersionLine(appVersion: DiagnosticsOverlaySnapshot['appVersion']): string {
  return truncateLabel(
    appVersion ? `version: ${appVersion.displayLabel}` : 'version: unavailable',
    MAX_SUMMARY_LINE_LENGTH
  );
}

export function formatVisualSummaryLines(visual: DiagnosticsOverlaySnapshot['visual']): string[] {
  if (!visual) {
    return ['visual: n/a'];
  }

  const status = `visual: load=${visual.loadedKeys.length}/${visual.manifestKeys.length} fallback=${visual.fallbackKeys.length} fail=${visual.failedKeys.length}`;
  const detail = `  loaded[${formatKeyList(visual.loadedKeys)}] fb[${formatKeyList(visual.fallbackKeys)}]`;
  const requiredFailures = visual.requiredFailures.length > 0
    ? `  requiredFail[${formatKeyList(visual.requiredFailures)}]`
    : `  skippedOpt=${visual.skippedOptionalKeys.length}`;

  return [
    truncateLabel(status, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(detail, MAX_SUMMARY_LINE_LENGTH),
    truncateLabel(requiredFailures, MAX_SUMMARY_LINE_LENGTH)
  ];
}

export class DiagnosticsOverlay extends Phaser.GameObjects.Container {
  private readonly background: Phaser.GameObjects.Rectangle;
  private readonly text: Phaser.GameObjects.Text;
  private readonly logger = createLogger('DiagnosticsOverlay');
  private lastProfileWarningSignature: string | null = null;
  private hasLoggedVersionMetadata = false;

  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);

    this.setDepth(900);
    this.setScrollFactor(0);
    this.setVisible(false);
    this.setPosition(scene.scale.width - PANEL_WIDTH - 12, scene.scale.height - PANEL_HEIGHT - 44);

    this.background = scene.add.rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT, 0x10151c, 0.84);
    this.background.setOrigin(0, 0);
    const strokableBackground = this.background as Phaser.GameObjects.Rectangle & {
      setStrokeStyle?: (lineWidth: number, color?: number, alpha?: number) => Phaser.GameObjects.Rectangle;
    };
    if (typeof strokableBackground.setStrokeStyle === 'function') {
      strokableBackground.setStrokeStyle(1, 0xffffff, 0.25);
    }
    this.text = scene.add.text(PANEL_PADDING, PANEL_PADDING, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '12px',
      color: '#d6f0ff',
      align: 'left',
      fixedWidth: TEXT_WIDTH,
      fixedHeight: TEXT_HEIGHT,
      maxLines: 33,
      wordWrap: { width: TEXT_WIDTH, useAdvancedWrap: true }
    }).setOrigin(0, 0);

    this.add([this.background, this.text]);
    scene.add.existing(this);
    this.logger.debug('constructor:created', { panelWidth: PANEL_WIDTH, panelHeight: PANEL_HEIGHT });
  }

  public setPanelVisible(isVisible: boolean): void {
    this.setVisible(isVisible);
    this.logger.debug('setPanelVisible', { isVisible });
  }

  public resize(): void {
    this.setPosition(this.scene.scale.width - PANEL_WIDTH - 12, this.scene.scale.height - PANEL_HEIGHT - 44);
  }

  public updateSnapshot(snapshot: DiagnosticsOverlaySnapshot): void {
    const poolSummary = ['bullet', 'orbit_orb', 'weapon_area', 'weapon_beam', 'weapon_boomerang', 'weapon_chain']
      .map(type => {
        const pool = snapshot.poolStats[type];
        if (!pool) return `${type}: n/a`;
        return `${type}: ${pool.active}/${pool.total}`;
      })
      .join('\n');

    const scalingSummaryLines = formatScalingSummaryLines(snapshot.scalingSummary);
    const bossEncounterSummaryLines = formatBossEncounterSummaryLines(snapshot);
    const debugCommandLines = formatDebugCommandSummaryLines(snapshot.lastDebugCommandResult, snapshot.lastDebugWaveAction ?? null);
    const inventoryLines = formatInventorySummaryLines(snapshot.inventory);
    const playerStatsLines = formatPlayerStatsSummaryLines(snapshot.playerStatsSnapshot);
    const visualLines = formatVisualSummaryLines(snapshot.visual);
    const profile = snapshot.profile;
    const appVersion = snapshot.appVersion;
    const versionLine = formatAppVersionLine(appVersion);
    const profileLines = profile?.ok ? [
      `profile: v${profile.schemaVersion} runs=${profile.totalRuns} unlocks=${profile.unlockCount}`,
      `  best=${Math.floor(profile.bestSurvivedSeconds ?? 0)}s last=${profile.lastRunResult ?? 'none'}`
    ] : [`profile: unavailable${profile?.error ? ` ${profile.error}` : ''}`];
    if (profile?.ok) {
      this.lastProfileWarningSignature = null;
    } else if (profile) {
      const warningSignature = profile.error ?? 'unknown';
      if (shouldEmitProfileDiagnosticsWarning(this.lastProfileWarningSignature, warningSignature)) {
        this.lastProfileWarningSignature = warningSignature;
        this.logger.warn('updateSnapshot:profile-incomplete', { error: profile.error });
      }
    }
    if (appVersion && !this.hasLoggedVersionMetadata) {
      this.logger.debug('updateSnapshot:version-metadata-available', appVersion);
      this.hasLoggedVersionMetadata = true;
    } else if (!appVersion) {
      this.logger.warn('updateSnapshot:version-metadata-missing');
    }
    this.logger.debug('updateSnapshot:scaling-summary-formatted', {
      lineCount: scalingSummaryLines.length,
      enemyId: snapshot.scalingSummary?.enemyId,
      commandLines: debugCommandLines.length,
      inventoryLines: inventoryLines.length,
      playerStatsLines: playerStatsLines.length,
      visualLines: visualLines.length
    });

    this.text.setText([
      `fps: ${snapshot.fps.toFixed(1)}`,
      `playerHp: ${snapshot.playerHp}`,
      `enemies: ${snapshot.enemyCount}`,
      `wave: #${snapshot.waveIndex} t=${snapshot.waveTime.toFixed(1)}`,
      `boss: active=${snapshot.bossActive ? 'yes' : 'no'} spawned=${snapshot.bossesSpawned}`,
      `bossCfg: t=${snapshot.bossSpawnTime}s count=${snapshot.bossesToWin} killed=${snapshot.bossesKilled}`,
      `testing: ${snapshot.testingMode.enabled ? 'on' : 'off'}`,
      `  noDamage=${snapshot.testingMode.disablePlayerDamage} noDeath=${snapshot.testingMode.disablePlayerDeath}`,
      `  earlyBoss=${snapshot.testingMode.earlyBoss} pauseWaves=${snapshot.pauseWaves}`,
      `  combatDebug=${snapshot.testingMode.verboseCombatDiagnostics} noCamFx=${snapshot.testingMode.suppressCameraFeedback}`,
      ...profileLines,
      versionLine,
      ...bossEncounterSummaryLines,
      ...scalingSummaryLines,
      ...debugCommandLines,
      ...inventoryLines,
      ...playerStatsLines,
      ...visualLines,
      poolSummary
    ].join('\n'));
  }
}

function formatKeyList(keys: readonly string[]): string {
  if (keys.length === 0) {
    return 'none';
  }

  const visibleKeys = keys
    .slice(0, MAX_VISUAL_KEYS)
    .map(key => truncateLabel(key, MAX_VISUAL_KEY_LENGTH));
  const remaining = Math.max(0, keys.length - visibleKeys.length);
  return remaining > 0 ? `${visibleKeys.join(',')},+${remaining}` : visibleKeys.join(',');
}

function truncateLabel(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  if (maxLength <= 3) {
    return value.slice(0, maxLength);
  }

  return `${value.slice(0, maxLength - 3)}...`;
}
